import { BALANCE } from '../balance.ts';
import type { Rng } from '../rng.ts';
import type { Direction4, PuzzleAct, PuzzleInstance, Role } from '../types.ts';
import {
  failResult,
  okResult,
  pickHolder,
  shown,
  type ActContext,
  type GenContext,
  type PuzzleModule,
  type SolveInfo,
  type TamperChoice,
  type ViewContext,
} from './types.ts';
import { alterableClues, clueByKind, holderClues, playersWithRole } from './util.ts';

/**
 * Laser (GDD §8.5). The locksmith flips mirrors in the room but cannot see the beam; the
 * hacker's terminal previews where the beam goes for the *current* mirror setting. They must
 * talk. Firing a beam that touches the display case costs alarm.
 */

export interface Cell {
  x: number;
  y: number;
}

export type Mirror = '/' | '\\';

export interface LaserParams {
  size: number;
  emitter: Cell & { dir: Direction4 };
  receiver: Cell;
  caseCell: Cell;
  obstacles: Cell[];
  mirrors: Cell[];
  solution: Mirror[];
}

export interface LaserState {
  orient: Mirror[];
  lastFireMs: number;
}

export type BeamEnd = 'receiver' | 'case' | 'obstacle' | 'edge' | 'loop';

export interface Beam {
  cells: Cell[];
  end: BeamEnd;
}

const VEC: Record<Direction4, Cell> = {
  n: { x: 0, y: -1 },
  e: { x: 1, y: 0 },
  s: { x: 0, y: 1 },
  w: { x: -1, y: 0 },
};

const SIZE_BY_LEVEL = [7, 7, 7, 8, 8, 9] as const;
const MIRRORS_BY_LEVEL = [2, 2, 3, 3, 4, 5] as const;
const OBSTACLES_BY_LEVEL = [0, 0, 1, 2, 3, 4] as const;

const params = (p: PuzzleInstance) => p.params as LaserParams;
const state = (p: PuzzleInstance) => p.state as LaserState;

const sameCell = (a: Cell, b: Cell) => a.x === b.x && a.y === b.y;

/** `/` turns right-going beams up; `\` turns right-going beams down. */
function reflect(v: Cell, m: Mirror): Cell {
  return m === '/' ? { x: -v.y, y: -v.x } : { x: v.y, y: v.x };
}

/** Follows the beam from the emitter through the current mirror setting. */
export function traceBeam(par: LaserParams, orient: readonly Mirror[]): Beam {
  const cells: Cell[] = [];
  let pos: Cell = { x: par.emitter.x, y: par.emitter.y };
  let v = VEC[par.emitter.dir];
  for (let step = 0; step < par.size * par.size * 2; step++) {
    pos = { x: pos.x + v.x, y: pos.y + v.y };
    if (pos.x < 0 || pos.y < 0 || pos.x >= par.size || pos.y >= par.size) {
      return { cells, end: 'edge' };
    }
    cells.push(pos);
    if (par.obstacles.some((o) => sameCell(o, pos))) return { cells, end: 'obstacle' };
    if (sameCell(par.caseCell, pos)) return { cells, end: 'case' };
    if (sameCell(par.receiver, pos)) return { cells, end: 'receiver' };
    const mi = par.mirrors.findIndex((m) => sameCell(m, pos));
    if (mi >= 0) v = reflect(v, orient[mi] as Mirror);
  }
  return { cells, end: 'loop' };
}

function turnMirror(vin: Cell, vout: Cell): Mirror {
  const slash = reflect(vin, '/');
  return slash.x === vout.x && slash.y === vout.y ? '/' : '\\';
}

function tryBuild(rng: Rng, level: number): LaserParams | null {
  const size = SIZE_BY_LEVEL[level] ?? 7;
  const nMirrors = MIRRORS_BY_LEVEL[level] ?? 3;
  const key = (c: Cell) => c.y * size + c.x;

  const emitter = { x: 0, y: rng.int(1, size - 2), dir: 'e' as Direction4 };
  let pos: Cell = { x: emitter.x, y: emitter.y };
  let v: Cell = VEC.e;
  const used = new Set<number>([key(pos)]);
  const mirrors: Cell[] = [];
  const mirrorTypes: Mirror[] = [];
  const pathCells: Cell[] = [];

  const advance = (len: number): boolean => {
    for (let i = 0; i < len; i++) {
      const next = { x: pos.x + v.x, y: pos.y + v.y };
      if (next.x < 0 || next.y < 0 || next.x >= size || next.y >= size) return false;
      if (used.has(key(next))) return false;
      used.add(key(next));
      pathCells.push(next);
      pos = next;
    }
    return true;
  };

  for (let i = 0; i < nMirrors; i++) {
    if (!advance(rng.int(2, 4))) return null;
    mirrors.push({ ...pos });
    const turns = rng.shuffle([
      { x: v.y, y: -v.x },
      { x: -v.y, y: v.x },
    ]);
    const nv = turns[0] as Cell;
    mirrorTypes.push(turnMirror(v, nv));
    v = nv;
  }
  if (!advance(rng.int(2, 3))) return null;
  const receiver = { ...pos };
  // The receiver is the last path cell, so it is not part of the mirrors.
  const pathKeys = new Set(pathCells.map(key));

  const adjacent: Cell[] = [];
  for (const c of pathCells) {
    for (const d of Object.values(VEC)) {
      const q = { x: c.x + d.x, y: c.y + d.y };
      if (q.x < 0 || q.y < 0 || q.x >= size || q.y >= size) continue;
      if (pathKeys.has(key(q)) || key(q) === key(emitter)) continue;
      adjacent.push(q);
    }
  }
  if (adjacent.length === 0) return null;
  const caseCell = rng.pick(adjacent);

  const obstacles: Cell[] = [];
  const nObstacles = OBSTACLES_BY_LEVEL[level] ?? 0;
  const free: Cell[] = [];
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const k = y * size + x;
      if (!pathKeys.has(k) && k !== key(emitter) && k !== key(caseCell)) free.push({ x, y });
    }
  }
  for (const c of rng.shuffle(free).slice(0, nObstacles)) obstacles.push(c);

  const par: LaserParams = {
    size,
    emitter,
    receiver,
    caseCell,
    obstacles,
    mirrors,
    solution: mirrorTypes,
  };
  const beam = traceBeam(par, par.solution);
  if (beam.end !== 'receiver') return null;
  return par;
}

function generate(ctx: GenContext) {
  const { rng, level } = ctx;
  for (let attempt = 0; attempt < 500; attempt++) {
    const par = tryBuild(rng, level);
    if (!par) continue;
    // Start with some mirrors wrong so the beam does not already reach the receiver.
    const orient = par.solution.map((m) => m);
    const flips = rng.int(1, par.mirrors.length);
    for (const i of rng.shuffle(par.mirrors.map((_, i) => i)).slice(0, flips)) {
      orient[i] = orient[i] === '/' ? '\\' : '/';
    }
    if (traceBeam(par, orient).end === 'receiver') continue;
    const s: LaserState = { orient, lastFireMs: -BALANCE.laserRechargeMs };
    return {
      params: par,
      state: s,
      clues: [
        {
          id: 'preview',
          kind: 'laserPreview',
          holder: pickHolder(ctx.roles, ['hacker']),
          // The preview is computed live; a tamper stores {ghostFlip: mirrorIndex}.
          value: {},
        },
      ],
      solvers: ['locksmith', 'hacker'] as Role[],
    };
  }
  throw new Error('laser generator failed');
}

function apply(p: PuzzleInstance, act: PuzzleAct, ctx: ActContext) {
  const par = params(p);
  const st = state(p);
  if (act.kind !== 'laser.flip' && act.kind !== 'laser.fire') {
    return failResult('invalid', 'not a laser action');
  }
  if (ctx.actorRole !== 'locksmith')
    return failResult('notAllowed', 'only the locksmith handles mirrors');
  const audience = playersWithRole(ctx.players, 'locksmith', 'hacker');
  if (act.kind === 'laser.flip') {
    if (!Number.isInteger(act.mirror) || act.mirror < 0 || act.mirror >= par.mirrors.length) {
      return failResult('invalid', 'no such mirror');
    }
    st.orient[act.mirror] = st.orient[act.mirror] === '/' ? '\\' : '/';
    return okResult({ events: [{ type: 'laser.flip', audience, data: { mirror: act.mirror } }] });
  }
  if (ctx.execMs - st.lastFireMs < BALANCE.laserRechargeMs) {
    return failResult('cooldown', 'the emitter is recharging');
  }
  st.lastFireMs = ctx.execMs;
  const beam = traceBeam(par, st.orient);
  if (beam.end === 'receiver') {
    return okResult({ solved: true, events: [{ type: 'laser.hit', audience, data: {} }] });
  }
  if (beam.end === 'case') {
    return okResult({
      errorTenths: BALANCE.penalty.laser,
      undo: { kind: 'none' },
      events: [{ type: 'laser.case', audience, data: {} }],
    });
  }
  return okResult({ events: [{ type: 'laser.miss', audience, data: { end: beam.end } }] });
}

function view(p: PuzzleInstance, ctx: ViewContext): Record<string, unknown> {
  const par = params(p);
  const st = state(p);
  const base: Record<string, unknown> = {
    type: 'laser',
    solved: p.solved,
    size: par.size,
    clues: {},
  };
  const room = {
    emitter: par.emitter,
    receiver: par.receiver,
    caseCell: par.caseCell,
    obstacles: par.obstacles,
    mirrors: par.mirrors.map((m, i) => ({ ...m, orient: st.orient[i] })),
  };
  if (ctx.role === 'locksmith') {
    // The locksmith stands in the room: sees the hardware, never the beam.
    base['room'] = room;
    base['lastFireMs'] = st.lastFireMs;
  }
  if (ctx.role === 'hacker') {
    const clue = clueByKind(p, 'laserPreview');
    const ghost = (shown(clue) as { ghostFlip?: number }).ghostFlip;
    const orient = st.orient.slice();
    if (ghost !== undefined && ghost >= 0 && ghost < orient.length) {
      orient[ghost] = orient[ghost] === '/' ? '\\' : '/';
    }
    const beam = traceBeam(par, orient);
    base['preview'] = {
      size: par.size,
      emitter: par.emitter,
      receiver: par.receiver,
      caseCell: par.caseCell,
      obstacles: par.obstacles,
      mirrorPositions: par.mirrors,
      beam: beam.cells,
      end: beam.end,
    };
  }
  base['clues'] = holderClues(p, ctx.role);
  if (ctx.revealed) base['secret'] = { solution: par.solution };
  return base;
}

function legalActs(p: PuzzleInstance, role: Role): PuzzleAct[] {
  if (role !== 'locksmith' || p.solved) return [];
  const out: PuzzleAct[] = params(p).mirrors.map((_, mirror) => ({
    kind: 'laser.flip' as const,
    mirror,
  }));
  out.push({ kind: 'laser.fire' });
  return out;
}

function tamper(p: PuzzleInstance, rng: Rng, exclude: Role | null): TamperChoice | null {
  if (alterableClues(p, exclude).length === 0) return null;
  const clue = clueByKind(p, 'laserPreview');
  return { clueId: clue.id, value: { ghostFlip: rng.int(0, params(p).mirrors.length - 1) } };
}

function solve(raw: unknown): SolveInfo {
  const par = raw as LaserParams;
  const beam = traceBeam(par, par.solution);
  return {
    solvable: beam.end === 'receiver',
    difficulty: par.mirrors.length + par.obstacles.length,
  };
}

function autoSolve(p: PuzzleInstance): void {
  state(p).orient = params(p).solution.slice();
}

export const laserModule: PuzzleModule = {
  type: 'laser',
  generate,
  apply,
  view,
  legalActs,
  tamper,
  autoSolve,
  undo: () => undefined,
  solve,
};
